import { Stack, SxProps } from '@mui/material';
import { LiveStatusIndicatorV2 } from 'common/ui';
import { Header } from 'common/ui/Header';
import { useGetCandidateBatchWithSubscription } from 'modules/analyse/hooks/useGetCandidateBatchWithSubscription';
import { useAnalyseDetailsCandidates } from 'modules/analyse/hooks/useAnalyseDetailsCandidates';
import { AnalyseTabsValue } from 'modules/analyse/constants/tabsValue';
import { CandidatesTable } from 'modules/analyse/components/CandidatesTable';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  CandidateRejectionModal,
  CandidateShortListingModal
} from 'services/candidate/components/CandidateModals';
import {
  useInterviewStagesFetcher,
  useRejectedReasonsFetcher
} from 'services/integration/hooks';
import { Paths } from 'services/navigation/paths';
import { formatToDate } from 'utils/datetime';
import { CandidatesRankingActions } from 'pages/CandidatesRanking/components/CandidatesRankingActions';
import { sanitizeObject } from 'utils/fomatting';
import { AnalysisAlert } from 'modules/analyse/components/AnalysisAlert';
import { useArchivedActionCampaign } from 'modules/analyse/hooks/useArchivedActionCampaign';
import {
  CandidateBatchStatus,
  SearchableCandidateSortableFields,
  SearchableSortDirection
} from 'generated-graphql/types';
import { Loading } from 'common/ui/Loading';
import { useOrganizationConfig } from 'core/stores/hooks/useOrganizationConfig';
import { useTracking } from 'services/tracking/hooks';
import { TrackingEventName } from 'services/tracking/constants';
import { RulesetLink } from 'modules/analyse/components/RulesetLink';
import { TabsV2 } from 'common/ui/TabsV2';
import { StarIcon } from 'common/icons/StarIcon';
import { SourcingAnalyseDetailsButton } from 'modules/analyse/components/SourcingAnalyseDetailsButton';
import { useAnalyseDetailsTabTotals } from './hooks/useAnalyseDetailsTabTotals';
import { TableV3Filters } from 'common/table/components/TableV3';
import { CandidatesTableColumns } from 'modules/analyse/tables/types/CandidatesTable';
import { TableV3Sort } from 'common/table/types/TableV3';
import { getCandidatesFilters } from 'modules/analyse/utils/getCandidatesFilters';
import { useRecoilValue } from 'recoil';
import { integrationAtom } from 'services/integration/atom';
import { SourcingCandidatesEmptyState } from 'modules/sourcing-shared/tables/states/SourcingCandidatesEmptyState';
import { SourceCandidatesTab } from 'modules/analyse/components/SourceCandidatesTab';
import { EmptyState } from 'system/atoms/empty-states/EmptyState';
import { getAnalysisTitle } from 'utils/getAnalysisTitle';
import { Button } from 'system/atoms';
import { WorkflowBuilderIcon } from 'common/icons/WorkflowBuilderIcon';
import { workflowBuilderLink } from 'modules/layout/constants/navbar';
import { buildWorkflowBuilderUrl } from 'modules/layout/utils/buildWorkflowBuilderUrl';

const emptyStateContainer: SxProps = {
  alignItems: 'center',
  justifyContent: 'center',
  gap: '26px',
  py: '60px'
};

const AnalyseDetailsContent: React.FC<{ id: string }> = ({ id }) => {
  const organizationConfig = useOrganizationConfig();
  const integration = useRecoilValue(integrationAtom);
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const [tab, setTab] = useState(
    searchParams.get('tab')
      ? (searchParams.get('tab') as AnalyseTabsValue)
      : AnalyseTabsValue.ALL_APPLICANTS
  );

  // Sync tab state when URL search params change
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      setTab(tabParam as AnalyseTabsValue);
    }
  }, [searchParams]);

  const [filters, setFilters] = useState<
    TableV3Filters<CandidatesTableColumns>
  >({});
  const [pageSize, setPageSize] = useState(50);
  const [from, setFrom] = useState(0);
  const [sort, setSort] = useState<
    TableV3Sort<SearchableCandidateSortableFields>
  >({
    field: SearchableCandidateSortableFields.TotalScoreScaled,
    direction: 'desc'
  });

  const currentPage = from / pageSize;

  const handleChangePage = (page: number) => {
    setFrom(page * pageSize);
  };

  const handleChangePageSize = (newPageSize: number) => {
    setPageSize(newPageSize);
    setFrom(0);
  };

  const {
    candidateBatch,
    updateQuery,
    subscriptionError: candidateBatchSubscriptionError
  } = useGetCandidateBatchWithSubscription({
    id: id as string
  });

  const isRecalculatingScores = candidateBatch?.isRecalculatingScores;

  const queryFilters = getCandidatesFilters({
    candidateBatchId: id ?? '',
    filters
  });

  const sortInput = {
    field: sort.field,
    direction:
      sort.direction === 'asc'
        ? SearchableSortDirection.Asc
        : SearchableSortDirection.Desc
  };

  const { currentTabData, tabCounts, subscriptionError } =
    useAnalyseDetailsCandidates({
      candidateBatchId: id,
      filter: queryFilters,
      sort: sortInput,
      limit: pageSize,
      from,
      currentTab: tab,
      isRecalculatingScores: !!isRecalculatingScores
    });

  useRejectedReasonsFetcher(candidateBatch?.atsJobId);
  useInterviewStagesFetcher(candidateBatch?.atsJobId);

  const { track } = useTracking();

  const { sourcedCandidatesTotal } = useAnalyseDetailsTabTotals({
    organizationId: candidateBatch?.organizationId ?? '',
    candidateBatchId: id ?? ''
  });

  const { archivedCampaignNames } = useArchivedActionCampaign(id);

  if (!candidateBatch) {
    return <Loading />;
  }

  const title = getAnalysisTitle(candidateBatch, 'Candidate ranking');
  const employerName = candidateBatch?.jobDescriptionParsed?.employerName ?? '';
  const lastModified = candidateBatch?.updatedAt
    ? formatToDate(candidateBatch.updatedAt)
    : 'N/A';

  const candidatesToProcessCount =
    candidateBatch?.candidatesToProcessCount ?? 0;
  const candidatesCount = candidateBatch?.candidatesCount ?? 0;
  const candidatesRemainingCount = candidatesToProcessCount - candidatesCount;

  const isArchived = candidateBatch?.status === CandidateBatchStatus.Archived;
  // The workflow builder owns a workflow-managed batch, so Classic only reads it.
  const isWorkflowManaged = !!candidateBatch.isWorkflowManaged;
  const isReadOnly = isArchived || isWorkflowManaged;
  const candidateBatchGroup = candidateBatch?.candidateBatchGroup;

  const headerItems = [
    employerName,
    candidateBatchGroup && !isWorkflowManaged && (
      <RulesetLink candidateBatchGroup={candidateBatchGroup} />
    ),
    `Last modified ${lastModified}`,
    candidateBatch?.externalId ?? null,
    candidateBatch?.atsJobReference
      ? `Job Ref ${candidateBatch?.atsJobReference}`
      : null,
    candidateBatch?.atsJobId ? `Job ID ${candidateBatch?.atsJobId}` : null,
    <LiveStatusIndicatorV2
      isLive={
        !subscriptionError && !candidateBatchSubscriptionError && !isArchived
      }
    />
  ];

  const noCandidates = candidatesToProcessCount === 0 && candidatesCount === 0;

  const element = (
    <>
      <>
        <Header
          currentBreadcrumb={title}
          breadcrumbs={[
            {
              name: 'Analyse',
              path: Paths.ANALYSE
            }
          ]}
          title={title}
          subtitle={candidateBatch?.jobDescriptionParsed?.location}
          items={headerItems}
          onBack={() => {
            navigate(-1);
          }}
          actions={[
            isWorkflowManaged ? (
              // Without a workflowId the link opens only the workflows list.
              candidateBatch.workflowId && (
                <Button
                  variant="tertiary"
                  onClick={() =>
                    window.open(
                      buildWorkflowBuilderUrl(
                        workflowBuilderLink,
                        candidateBatch.organizationId,
                        candidateBatch.workflowId
                      ),
                      '_blank'
                    )
                  }
                  startIcon={<WorkflowBuilderIcon size={14} />}
                >
                  View in workflow
                </Button>
              )
            ) : (
              <SourcingAnalyseDetailsButton
                candidateBatchId={candidateBatch.id}
              />
            )
          ]}
        />

        <AnalysisAlert
          status={candidateBatch?.status}
          isRecalculatingScores={!!isRecalculatingScores}
          candidatesRemainingCount={candidatesRemainingCount}
          archivedCampaignNames={archivedCampaignNames}
        />
        <Stack direction="column" gap="32px">
          <TabsV2
            tabs={[
              {
                label: 'All applicants',
                value: AnalyseTabsValue.ALL_APPLICANTS,
                count: tabCounts.allApplicants
              },
              {
                label: 'Met knockout requirements',
                value: AnalyseTabsValue.CANDIDATES_WITH_MINIMUM_REQUIREMENTS,
                count: tabCounts.withRequirements,
                startIcon: <StarIcon width={20} height={20} />
              },
              {
                label: 'Invited',
                value: AnalyseTabsValue.SOURCE_CANDIDATES,
                count: sourcedCandidatesTotal
              }
            ]}
            value={tab}
            onChange={(value) => {
              if (value === AnalyseTabsValue.SOURCE_CANDIDATES) {
                track(TrackingEventName.AnalyseDashboardInvitedTabClick, {
                  candidateBatchId: candidateBatch.id
                });
              }

              // Clear filters and update tab in a single operation
              const newParams = new URLSearchParams(searchParams);
              newParams.delete('searchFields');
              newParams.delete('filterValues');
              newParams.set('tab', value);
              setSearchParams(newParams.toString(), {
                state: { shallow: true },
                replace: true
              });

              // Reset filters state
              setFilters({});

              setTab(value as AnalyseTabsValue);
            }}
            endItems={
              tab !== AnalyseTabsValue.SOURCE_CANDIDATES
                ? [
                    <Stack
                      direction="row"
                      justifyContent="flex-end"
                      alignItems="center"
                    >
                      <CandidatesRankingActions
                        candidateBatch={candidateBatch}
                        setCandidateBatch={(newCandidateBatch) => {
                          updateQuery((prevData) => {
                            if (prevData) {
                              return {
                                ...prevData,
                                ...sanitizeObject(newCandidateBatch)
                              };
                            }

                            return prevData;
                          });
                        }}
                      />
                    </Stack>
                  ]
                : undefined
            }
          />
          {tab !== AnalyseTabsValue.SOURCE_CANDIDATES && (
            <>
              <CandidatesTable
                searchParams={searchParams}
                setSearchParams={setSearchParams}
                isLoading={currentTabData.loading}
                integration={integration}
                candidates={currentTabData.items}
                paginationTotalRows={currentTabData.total}
                currentPage={currentPage}
                onChangePage={handleChangePage}
                onChangePageSize={handleChangePageSize}
                onFiltersChanged={(newFilters) => {
                  setFilters(newFilters);
                  setFrom(0);
                }}
                onSort={setSort}
                sort={sort}
                isReadOnly={isReadOnly}
                emptyStateComponent={
                  noCandidates &&
                  organizationConfig?.sourcingEnabled &&
                  !isWorkflowManaged ? (
                    <Stack
                      sx={{
                        ...emptyStateContainer,
                        backgroundColor: 'customGrey.light'
                      }}
                    >
                      <SourcingCandidatesEmptyState
                        candidateBatchId={id as string}
                      />
                    </Stack>
                  ) : (
                    <EmptyState
                      type="no-results"
                      title="No applicants to show"
                    />
                  )
                }
              />
            </>
          )}
          {tab === AnalyseTabsValue.SOURCE_CANDIDATES && (
            <SourceCandidatesTab
              candidateBatchId={id}
              totalSourcedCandidates={sourcedCandidatesTotal}
            />
          )}
          <CandidateRejectionModal atsJobId={candidateBatch?.atsJobId} />
          <CandidateShortListingModal atsJobId={candidateBatch?.atsJobId} />
        </Stack>
      </>
    </>
  );
  return element;
};

export const AnalyseDetailsPage = () => {
  const { id } = useParams();
  return <AnalyseDetailsContent key={id} id={id as string} />;
};
